/**
 * Game Options Tab - Future slot machine features (Coming Soon)
 */

import {
  Layout,
  Grid3X3,
  Sparkles,
  Target,
  Gift,
  Gamepad2,
  Lock,
  Star,
  Zap,
  Repeat,
  ShoppingCart,
  Dices,
} from 'lucide-react';
import type { GameOptionsConfig } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface GameOptionsTabProps {
  config: GameOptionsConfig;
  onUpdate: (config: GameOptionsConfig) => void;
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  comingSoon?: boolean;
  children?: React.ReactNode;
}

function FeatureCard({ title, description, icon: Icon, comingSoon = true, children }: FeatureCardProps) {
  return (
    <div className={`border rounded-lg p-4 ${comingSoon ? 'opacity-60 bg-gray-50' : 'bg-white'}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${comingSoon ? 'bg-gray-200' : 'bg-primary-100'}`}>
          <Icon size={20} className={comingSoon ? 'text-gray-500' : 'text-primary-600'} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">{title}</h4>
            {comingSoon && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                <Lock size={10} /> Coming Soon
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
          {children && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </div>
  );
}

export default function GameOptionsTab({ config, onUpdate }: GameOptionsTabProps) {
  const updateField = <K extends keyof GameOptionsConfig>(field: K, value: GameOptionsConfig[K]) => {
    onUpdate({ ...config, [field]: value });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-4">Game Options</h2>
        <p className="text-sm text-gray-500 mb-6">
          Configure advanced slot machine features. Many options are coming soon and will unlock
          a wide variety of slot machine mechanics.
        </p>
      </div>

      {/* Current Layout Settings */}
      <div>
        <h3 className="text-md font-medium mb-4">Layout Configuration</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reel Count</label>
            <select
              value={config.reelCount}
              onChange={(e) => updateField('reelCount', Number(e.target.value) as 3 | 5 | 6)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value={3}>3 Reels</option>
              <option value={5}>5 Reels</option>
              <option value={6}>6 Reels</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Row Count</label>
            <select
              value={config.rowCount}
              onChange={(e) => updateField('rowCount', Number(e.target.value) as 3 | 4 | 5 | 6)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value={3}>3 Rows</option>
              <option value={4}>4 Rows</option>
              <option value={5}>5 Rows</option>
              <option value={6}>6 Rows</option>
            </select>
          </div>
        </div>
      </div>

      {/* Layout Types */}
      <div>
        <h3 className="text-md font-medium mb-4">Layout Types</h3>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            title="Classic"
            description="Traditional fixed grid layout with standard reels"
            icon={Grid3X3}
            comingSoon={false}
          >
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="layout"
                checked={config.layout === 'classic'}
                onChange={() => updateField('layout', 'classic')}
                className="text-primary-600"
              />
              <span className="text-sm">Selected</span>
            </label>
          </FeatureCard>
          <FeatureCard
            title="Megaways"
            description="Variable row heights per reel (up to 117,649 ways)"
            icon={Layout}
          />
          <FeatureCard
            title="Cluster Pays"
            description="Win by forming clusters of adjacent symbols"
            icon={Grid3X3}
          />
          <FeatureCard
            title="Cascading"
            description="Winning symbols disappear and new ones fall"
            icon={Repeat}
          />
        </div>
      </div>

      {/* Payline Options */}
      <div>
        <h3 className="text-md font-medium mb-4">Payline Configuration</h3>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            title="Fixed Paylines"
            description="Traditional fixed payline patterns"
            icon={Layout}
            comingSoon={false}
          >
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="paylineType"
                checked={config.paylineType === 'fixed'}
                onChange={() => updateField('paylineType', 'fixed')}
                className="text-primary-600"
              />
              <span className="text-sm">Selected</span>
            </label>
          </FeatureCard>
          <FeatureCard
            title="Ways to Win"
            description="Win by matching symbols on adjacent reels"
            icon={Zap}
          />
          <FeatureCard
            title="Cluster Wins"
            description="Win by connecting 5+ symbols in any direction"
            icon={Grid3X3}
          />
        </div>
      </div>

      {/* Wild Features */}
      <div>
        <h3 className="text-md font-medium mb-4">Wild Symbol Features</h3>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            title="Expanding Wilds"
            description="Wilds expand to cover the entire reel"
            icon={Sparkles}
          />
          <FeatureCard
            title="Sticky Wilds"
            description="Wilds stay in place for multiple spins"
            icon={Star}
          />
          <FeatureCard
            title="Walking Wilds"
            description="Wilds move across reels with each spin"
            icon={Zap}
          />
          <FeatureCard
            title="Multiplier Wilds"
            description="Wilds apply a multiplier to wins"
            icon={Target}
          />
        </div>
      </div>

      {/* Scatter Features */}
      <div>
        <h3 className="text-md font-medium mb-4">Scatter Symbol Features</h3>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            title="Pays Anywhere"
            description="Scatter wins regardless of position"
            icon={Sparkles}
            comingSoon={false}
          >
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.scatterFeatures?.paysAnywhere}
                onChange={(e) => updateField('scatterFeatures', {
                  ...config.scatterFeatures,
                  paysAnywhere: e.target.checked,
                })}
                className="rounded border-gray-300 text-primary-600"
              />
              <span className="text-sm">Enabled</span>
            </label>
          </FeatureCard>
          <FeatureCard
            title="Feature Trigger"
            description="Scatter symbols trigger bonus features"
            icon={Gift}
          />
        </div>
      </div>

      {/* Bonus Games */}
      <div>
        <h3 className="text-md font-medium mb-4">Bonus Game Types</h3>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            title="Free Spins"
            description="Award free spins with potential multipliers"
            icon={Repeat}
            comingSoon={false}
          >
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="bonusType"
                checked={config.bonusGameType === 'free_spins'}
                onChange={() => updateField('bonusGameType', 'free_spins')}
                className="text-primary-600"
              />
              <span className="text-sm">Selected</span>
            </label>
          </FeatureCard>
          <FeatureCard
            title="Hold & Spin"
            description="Lock symbols in place and re-spin for prizes"
            icon={Lock}
          />
          <FeatureCard
            title="Pick Bonus"
            description="Pick from hidden items to reveal prizes"
            icon={Gift}
          />
          <FeatureCard
            title="Wheel Bonus"
            description="Spin a wheel for prizes and multipliers"
            icon={Dices}
          />
        </div>
      </div>

      {/* Special Features */}
      <div>
        <h3 className="text-md font-medium mb-4">Special Features</h3>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            title="Buy Bonus"
            description="Purchase instant access to bonus features"
            icon={ShoppingCart}
          />
          <FeatureCard
            title="Gamble Feature"
            description="Double-or-nothing option after wins"
            icon={Dices}
          />
          <FeatureCard
            title="Autoplay"
            description="Automated spin functionality"
            icon={Gamepad2}
            comingSoon={false}
          >
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.features?.autoplay}
                onChange={(e) => updateField('features', {
                  ...config.features,
                  autoplay: e.target.checked,
                })}
                className="rounded border-gray-300 text-primary-600"
              />
              <span className="text-sm">Enabled</span>
            </label>
          </FeatureCard>
          <FeatureCard
            title="Turbo Mode"
            description="Faster spin animations"
            icon={Zap}
          />
        </div>
      </div>

      {/* Free Spins Config (if enabled) */}
      {config.bonusGameType === 'free_spins' && (
        <div>
          <h3 className="text-md font-medium mb-4">Free Spins Configuration</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Count
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={config.freeSpins?.defaultCount || 10}
                  onChange={(e) => updateField('freeSpins', {
                    defaultCount: Number(e.target.value),
                    multiplier: config.freeSpins?.multiplier ?? 1,
                    retriggerable: config.freeSpins?.retriggerable ?? true,
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Multiplier
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={config.freeSpins?.multiplier || 1}
                  onChange={(e) => updateField('freeSpins', {
                    defaultCount: config.freeSpins?.defaultCount ?? 10,
                    multiplier: Number(e.target.value),
                    retriggerable: config.freeSpins?.retriggerable ?? true,
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retriggerable
                </label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={config.freeSpins?.retriggerable}
                    onChange={(e) => updateField('freeSpins', {
                      defaultCount: config.freeSpins?.defaultCount ?? 10,
                      multiplier: config.freeSpins?.multiplier ?? 1,
                      retriggerable: e.target.checked,
                    })}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-sm">Can retrigger</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Gamepad2 className="text-blue-500 flex-shrink-0" size={20} />
          <div>
            <h4 className="font-medium text-blue-800">More Features Coming Soon</h4>
            <p className="text-sm text-blue-600 mt-1">
              We're working on adding more slot machine mechanics including Megaways, cluster pays,
              cascading reels, and advanced bonus games. These features will allow you to create
              an endless variety of unique slot machine experiences.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
